/**
 * @file BackupTokenRevokeDialog.tsx
 * @description Экстренный отзыв ключа доступа агента бэкапа (phase19.26 шаг 7.1,
 * Р2) — когда ключ мог попасть к посторонним.
 *
 * Старый ключ гаснет сразу, ожидающий замены — сбрасывается. Агент получает
 * отказ панели, берёт настройки по умолчанию и продолжает делать локальные
 * копии, но не отчитывается в панель и не читает из неё настройки (включая
 * «Бэкап включён»), пока на сервере приложения заново не выполнят команду
 * установки. Действие необратимое, поэтому подтверждается явно: отметкой
 * «понимаю» и отдельной кнопкой. На < sm диалог на весь экран.
 */

import { ShieldOff } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { useNotify } from "../../ui/NotifyProvider";
import {
  backupErrorMessage,
  revokeBackupWebhookToken,
  type BackupWebhookInfoDto,
} from "../../../lib/api-backups";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Ключ отозван: `info` — сведения о ключе из ответа сервера. */
  onRevoked: (info: BackupWebhookInfoDto) => void;
}

export function BackupTokenRevokeDialog({ open, onClose, onRevoked }: Props) {
  const notify = useNotify();
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);

  function close() {
    setUnderstood(false);
    onClose();
  }

  async function revoke() {
    setBusy(true);
    try {
      const info = await revokeBackupWebhookToken();
      notify.success("Ключ отозван. Панель не видит агента — получите команду установки ниже и выполните её на сервере.");
      onRevoked(info);
      close();
    } catch (err) {
      notify.error(backupErrorMessage(err, "Не удалось отозвать ключ"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(o) => (!o && !busy ? close() : null)}>
      <AlertDialogContent fullScreen className="max-w-lg gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>Экстренный отзыв ключа</AlertDialogTitle>
          <AlertDialogDescription>
            Используйте, если ключ доступа агента мог попасть к посторонним. Старый ключ перестанет
            работать сразу. Агент продолжит делать копии на сервере с настройками по умолчанию, но
            перестанет отчитываться в панель и получать из неё настройки, пока вы не выполните на
            сервере команду установки заново.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl bg-surface-low px-3 py-2">
          <Checkbox
            checked={understood}
            onCheckedChange={(v) => setUnderstood(Boolean(v))}
            disabled={busy}
          />
          <span className="text-sm text-on-surface">
            Понимаю: панель не будет видеть копии и управлять ими до повторной установки агента
          </span>
        </label>

        <AlertDialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Отмена
          </Button>
          <Button
            // Окончательное подтверждение — единственная сплошная красная.
            variant="destructive-solid"
            onClick={() => void revoke()}
            loading={busy}
            disabled={!understood}
          >
            <ShieldOff className="size-4" />
            Отозвать ключ
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
